import { CartStatus } from '@models/cart';
import { ItemResponse } from '@models/item';
import { CreateOrderRequest, OptionWithGroup, OrderLine } from '@models/order';
import { ProjectPgVendor } from '@models/project';
import { ShopResponse } from '@models/shop';
import { PayloadAction, WritableDraft } from '@reduxjs/toolkit';
import { defaultOrderFields } from '@store/defaultOrderFields';
import getGlobalName from '@utils/getGlobalName';
import { toReward } from '@utils/orderUtils';

export interface PrepareOrderInfo {
    activityId: string;
    shop: ShopResponse;
    items: ItemResponse[];
    projectId: string;
}

const makeOptions = (
    item: ItemResponse,
    optionType: 'PERSONAL'|'DEFAULT',
): OptionWithGroup[] => {
    const optionGroups = optionType === 'PERSONAL'
        ? item.personalOptionGroups : item.defaultOptionGroups;
    // 모든 옵션의 종류를 추가
    return optionGroups.map((optionGroup) => {
        // 라디오이면서 옵션의 종류가 1개인 경우
        const isSingleOption = optionGroup.options.length === 1
            && optionGroup.uiButtonType === 'RADIO';
        const options = optionGroup.options
        // 기본값 또는 라디오(1개)인 경우 주문에 추가
            .filter((option) => option.isDefault || isSingleOption)
            .map((option) => {
                const {
                    optionId, name, salePrice, isChangeable, maxQuantity, posId,
                } = option;
                return {
                    shopOptionId: optionId,
                    optionName: name,
                    optionPrice: salePrice,
                    quantity: 1,
                    totalAmount: salePrice,
                    isChangeable,
                    optionGroupId: optionGroup.optionGroupId,
                    maxQuantity,
                    posId,
                    ...toReward(option.rewardPolicy),
                };
            });
        return {
            optionGroupId: optionGroup.optionGroupId,
            minSelection: optionGroup.minSelection,
            maxSelection: optionGroup.maxSelection,
            options,
        };
    });
};

export function prepare(state: WritableDraft<Record<string, CreateOrderRequest>>, action: PayloadAction<PrepareOrderInfo>) {
    const {
        activityId, shop, items, projectId,
    } = action.payload;

    const makeOrderItems = (): OrderLine[] => items?.map((item) => {
        const {
            id, name, salePrice, couponUsable,
        } = item;
        return {
            shopItemId: id,
            thumbnailImage: item.imageUrls?.[0] || '',
            defaultOptions: makeOptions(item, 'DEFAULT'),
            personalOptions: makeOptions(item, 'PERSONAL'),
            itemName: name,
            itemPrice: salePrice,
            totalAmount: salePrice,
            quantity: 1,
            itemImageUrl: item.imageUrls?.[0],
            couponUsable,
            posId: item.posId,
            status: CartStatus.ON_SALE,
            ...toReward(item.rewardPolicy),
        };
    }) || [];

    const newDefaultValue: CreateOrderRequest = {
        projectId,
        shopId: shop?.id || '',
        shopName: shop?.name || '',
        orderPath: 'DIRECT',
        orderType: 'TAKE_OUT',
        totalAmount: 0,
        totalDiscount: 0,
        totalCouponDiscount: 0,
        ...defaultOrderFields,
        totalPointDiscount: 0,
        orderLines: makeOrderItems(),
        paymentAmount: 0,
        paymentMethod: 'CREDIT_CARD',
        returnUrl: '',
    };
    state[activityId] = newDefaultValue;
}
